import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, TextField, Typography, MenuItem, Grid } from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";

const defaultCard = {
  name: "",
  number: "",
  expiry: "",
  cvv: "",
  installments: "1",
};

function cardDigits(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 19);
}

function formatCardNumber(digits) {
  return cardDigits(digits).replace(/(\d{4})(?=\d)/g, "$1 ").trim();
}

/** Exibe •••• •••• •••• 1234 mantendo só os últimos 4 dígitos visíveis. */
export function maskCardNumberDisplay(digits) {
  const d = cardDigits(digits);
  if (!d.length) return "";
  if (d.length <= 4) return d;
  const last4 = d.slice(-4);
  const hiddenLen = d.length - 4;
  const groups = Math.max(1, Math.ceil(hiddenLen / 4));
  const masked = Array.from({ length: groups }, () => "••••").join(" ");
  return `${masked} ${last4}`;
}

function formatExpiry(value) {
  const digits = String(value).replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

/** Máximo de parcelas conforme cadastro da forma de pagamento. */
export function getMaxInstallmentsForPaymentMethod(pm) {
  if (!pm) return 12;
  const sem = Number(pm.parcelas_semjuros) || 0;
  const com = Number(pm.parcelas_comjuros) || 0;
  const configured = Math.max(sem, com);
  if (configured > 0) return Math.min(12, configured);
  return 12;
}

export function clampInstallment(value, max) {
  const n = Math.max(1, Math.min(max, parseInt(String(value), 10) || 1));
  return String(n);
}

/**
 * Formulário de cartão para pagamento Rede (tipo 9) no balcão.
 */
const BPeRedeCardForm = ({ value, onChange, disabled = false, paymentMethod = null }) => {
  const { t } = useTranslation("chat");
  const card = value || defaultCard;
  const [numberFocused, setNumberFocused] = useState(false);
  const [cvvFocused, setCvvFocused] = useState(false);

  const maxInstallments = useMemo(
    () => getMaxInstallmentsForPaymentMethod(paymentMethod),
    [paymentMethod]
  );

  const installmentOptions = useMemo(
    () => Array.from({ length: maxInstallments }, (_, i) => i + 1),
    [maxInstallments]
  );

  const digits = cardDigits(card.number);
  const numberDisplay = numberFocused ? formatCardNumber(digits) : maskCardNumberDisplay(digits);

  const set = (field, raw) => {
    let v = raw;
    if (field === "number") v = formatCardNumber(raw);
    if (field === "expiry") v = formatExpiry(raw);
    if (field === "cvv") v = String(raw).replace(/\D/g, "").slice(0, 4);
    if (field === "installments") v = clampInstallment(raw, maxInstallments);
    onChange({ ...card, [field]: v });
  };

  const semJuros = Number(paymentMethod?.parcelas_semjuros) || 0;
  const comJuros = Number(paymentMethod?.parcelas_comjuros) || 0;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 1.5,
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
        <CreditCardIcon fontSize="small" />
        {t("bpeWizard.cardTitle")}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        {t("bpeWizard.cardHint")}
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <TextField
            label={t("bpeWizard.cardHolder")}
            value={card.name}
            onChange={(e) => set("name", e.target.value)}
            fullWidth
            size="small"
            disabled={disabled}
            required
            autoComplete="cc-name"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t("bpeWizard.cardNumber")}
            value={numberDisplay}
            onChange={(e) => set("number", e.target.value)}
            onFocus={() => setNumberFocused(true)}
            onBlur={() => setNumberFocused(false)}
            fullWidth
            size="small"
            disabled={disabled}
            inputProps={{
              inputMode: "numeric",
              autoComplete: "cc-number",
              style: numberFocused ? undefined : { letterSpacing: "0.06em" },
            }}
            helperText={!numberFocused && digits.length > 4 ? t("bpeWizard.cardNumberMaskHint") : " "}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label={t("bpeWizard.cardExpiry")}
            placeholder="MM/AA"
            value={card.expiry}
            onChange={(e) => set("expiry", e.target.value)}
            fullWidth
            size="small"
            disabled={disabled}
            inputProps={{ inputMode: "numeric", autoComplete: "cc-exp" }}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label={t("bpeWizard.cardCvv")}
            type={cvvFocused ? "text" : "password"}
            value={card.cvv}
            onChange={(e) => set("cvv", e.target.value)}
            onFocus={() => setCvvFocused(true)}
            onBlur={() => setCvvFocused(false)}
            fullWidth
            size="small"
            disabled={disabled}
            inputProps={{ inputMode: "numeric", autoComplete: "cc-csc", maxLength: 4 }}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            select
            label={t("bpeWizard.cardInstallments")}
            value={clampInstallment(card.installments, maxInstallments)}
            onChange={(e) => set("installments", e.target.value)}
            fullWidth
            size="small"
            disabled={disabled}
            helperText={
              semJuros > 0 || comJuros > 0
                ? t("bpeWizard.cardInstallmentsHint", {
                    max: maxInstallments,
                    semJuros: semJuros > 0 ? semJuros : "—",
                    comJuros: comJuros > 0 ? comJuros : "—",
                  })
                : t("bpeWizard.cardInstallmentsDefault", { max: maxInstallments })
            }
          >
            {installmentOptions.map((n) => (
              <MenuItem key={n} value={String(n)}>
                {n === 1 ? t("bpeWizard.installmentCash") : `${n}x`}
                {semJuros > 0 && n <= semJuros ? ` · ${t("bpeWizard.installmentNoFee")}` : ""}
                {comJuros > 0 && n > semJuros && n <= comJuros ? ` · ${t("bpeWizard.installmentWithFee")}` : ""}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </Box>
  );
};

export function validateRedeCard(card, t) {
  if (!card?.name?.trim()) return t("bpeWizard.errors.cardHolder");
  const num = cardDigits(card.number);
  if (num.length < 13) return t("bpeWizard.errors.cardNumber");
  const exp = String(card.expiry || "").trim();
  if (!/^\d{2}\/\d{2,4}$/.test(exp)) return t("bpeWizard.errors.cardExpiry");
  if (!card.cvv || String(card.cvv).length < 3) return t("bpeWizard.errors.cardCvv");
  return null;
}

export function cardToApiPayload(card) {
  const [expMonth, expYear] = String(card.expiry || "").split("/");
  return {
    holder: card.name?.trim(),
    number: cardDigits(card.number),
    expMonth,
    expYear,
    cvv: card.cvv,
    installments: Math.max(1, Math.min(12, parseInt(card.installments, 10) || 1)),
  };
}

export default BPeRedeCardForm;
